import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { FocusPhase } from '@/lib/focus/focusMachine';
import type { MoodOption } from '../mood/moods';
import { WELLBEING_PATHS } from '../wellbeingPaths';

/** What the hub suggests now - one thing, never a menu. */
export type RightNowState =
  | { kind: 'start' }
  | { kind: 'mood'; mood: MoodOption }
  | {
      kind: 'block';
      phase: Exclude<FocusPhase, 'idle'>;
      timeLabel: string;
      /** Flow counts up, so its time is "in", not "left". */
      countsUp: boolean;
      intention?: string;
    };

interface Step {
  line: string;
  action?: { label: string; to: string };
}

function blockStep(state: Extract<RightNowState, { kind: 'block' }>): Step {
  const on = state.intention ? ` on ${state.intention}` : '';
  const toFocus = WELLBEING_PATHS.focus;
  if (state.phase === 'break') {
    return {
      line: `You're on a break, ${state.timeLabel} left. Rest is part of learning.`,
      action: { label: 'Open your break', to: toFocus },
    };
  }
  if (state.phase === 'paused') {
    return {
      line: `Your block${on} is paused at ${state.timeLabel}. It will wait for you.`,
      action: { label: 'Back to your block', to: toFocus },
    };
  }
  return {
    line: state.countsUp
      ? `${state.timeLabel} in${on}. Keep going while it flows.`
      : `${state.timeLabel} left${on}. You're doing it.`,
    action: { label: 'Back to your block', to: toFocus },
  };
}

function stepFor(state: RightNowState): Step {
  if (state.kind === 'block') return blockStep(state);
  if (state.kind === 'mood') {
    const { response, action } = state.mood;
    return action.kind === 'support'
      ? { line: response }
      : {
          line: response,
          action: { label: action.label, to: WELLBEING_PATHS[action.kind] },
        };
  }
  return {
    line: 'A minute of slow breathing is a kind way to begin.',
    action: { label: 'Breathe now', to: WELLBEING_PATHS.breathe },
  };
}

/**
 * The hub's single suggestion, in place of badges on every tile: the running
 * block if there is one, otherwise the step today's mood points to, otherwise
 * a gentle default. (On a low mood the hub adds a line that opens the
 * helplines, full width under the check-in - see `HubHero`.)
 */
/** A low or overwhelmed answer: the hub offers people to talk to as well. */
export const offersSupport = (state: RightNowState) =>
  state.kind === 'mood' && Boolean(state.mood.low);

export function RightNow({ state }: { state: RightNowState }) {
  const step = stepFor(state);
  return (
    <section aria-labelledby='right-now-heading' className='space-y-3'>
      <h2
        id='right-now-heading'
        className='text-sm font-medium text-muted-foreground'
      >
        For right now
      </h2>
      {/* Polite: it changes when the student answers the mood question. */}
      <div aria-live='polite' className='space-y-4'>
        <p className='text-lg leading-snug text-foreground'>{step.line}</p>
        {step.action && (
          <Button asChild size='sm' className='max-sm:h-11'>
            <Link to={step.action.to}>
              {step.action.label}
              <ArrowRight className='ml-1.5 h-4 w-4' aria-hidden='true' />
            </Link>
          </Button>
        )}
      </div>
    </section>
  );
}
